// external dependencies
import React, { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { v4 as uuid } from "uuid";
import { useDispatch } from "react-redux";

// internal data providers, hooks, and state
import useAudioRecorder, { recorderControls, isRecordingError } from "@common/hooks/useAudioRecorder";
import { useLock, waitForUploadsToComplete } from "./LockProvider";
import useFileUpload from "@common/hooks/useFileUpload";
import usePatientState from "@/features/visits/list-view/state/usePatientState";
import { useRealtimeContextApi } from "@providers/RealtimeProvider";
import MobileBrowserRecordingWarning from "@/features/recording-widget/MobileBrowserRecordingWarning";
import useScreenWakeLock from "@common/hooks/useScreenWakeLock";
import {
    acknowledgeMobileBrowserRecording,
    hasAcknowledgedMobileBrowserRecording,
    isMobileBrowserRecordingDevice,
    storeUrlForDevice,
} from "@common/lib/mobileBrowserDevice";

// internal components and types
import {
    FeatureFlags,
    PatientRecord,
    RecordingState,
    DocumentAsset,
    DocumentAssetStatus,
    FileUploadingState,
    Contact,
    PatientRecordFetchErrors,
} from "@common/utils/types";
import { mergedIntoVisit } from "@/common/lib/mergedAwayVisits";
import { showActionToast } from "@/common/components/ActionToast";
import { COPY } from "@/features/merge-visits/copy";

/** Start refused because the visit was merged away; the page shows the merged-away notice instead. */
const MERGED_AWAY_START = "merged_away_start";
import { toast } from "react-toastify";
import { fetchPatientRecord } from "@/common/lib/supabaseClient";
import { uploadChunk, UploadDocumentAsset, uploadErrorMessageFor } from "@/common/lib/api";
import { useDataContextApi } from "./DataProvider";
import { useAppSelector } from "@/common/hooks/useRedux";
import { useAccountContext } from "./AccountProvider";
import { useImpersonationAwareUser } from "@/common/hooks/useImpersonationAwareUser";
import { setDocumentAssets, toggleAssetSelection } from "@/features/patient-view/state/redux/activePatientDataSlice";
import { voipAnalytics } from "@/common/utils/voipAnalytics";
import { useSession } from "@supabase/auth-helpers-react";
import { useIsNarrowViewport } from "@/common/hooks/useIsNarrowViewport";
import { readQuickStartMinimized } from "@/features/recording-widget/quickStartMinimizedPersistence";
import { useMic } from "@/features/recording-widget/mic/MicProvider";
import { MicStartError, type MicStateId } from "@/features/recording-widget/mic/micStatus";
import { micCopy } from "@/features/recording-widget/mic/micCopy";

/**
 * EN-1499: the record-press mic check is bounded. enumerateDevices or permissions.query can hang, and a
 * hung check must not block later presses, and costs the start at most the timeout. A timeout or a thrown check counts
 * as ok: the start's own getUserMedia then does the real check and reports through MicStartError.
 */
const MIC_GATE_TIMEOUT_MS = 300;
type MicGateResult = { ok: true } | { ok: false; state: MicStateId };
const checkMicWithTimeout = async (check: () => Promise<MicGateResult>): Promise<MicGateResult> => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<"timeout">((resolve) => {
        timer = setTimeout(() => resolve("timeout"), MIC_GATE_TIMEOUT_MS);
    });
    try {
        const result = await Promise.race([check(), timeout]);
        if (result === "timeout") {
            // The abandoned check keeps running. A late result only updates the idle mic state, and the
            // bar ignores idle mic state while recording, so it cannot interrupt a recording.
            Sentry.addBreadcrumb({ category: "recording", message: "recording.mic_gate_timeout", level: "info" });
            return { ok: true };
        }
        return result;
    } catch (error) {
        Sentry.addBreadcrumb({
            category: "recording",
            message: "recording.mic_gate_error",
            level: "info",
            data: { errorName: error instanceof Error ? error.name : undefined },
        });
        return { ok: true };
    } finally {
        clearTimeout(timer);
    }
};

/**
 * Context and Types
 **/
// recording widget states
export enum RecordingWidgetState {
    WAITING = "waiting",
    RECORDING = "recording",
    PAUSED = "paused",
    UPLOADING = "uploading",
    UPLOADING_ERROR = "uploading_error",
    SAVED = "saved",
    LOADING = "loading",
}

interface HandleStartRecordingParams {
    patientRecordId?: string | null;
    patientName?: string | null;
    showLoading?: boolean;
}

// controls recorder hook
export type recordingWidgetControls = {
    recordingWidgetState: RecordingWidgetState;
    setRecordingWidgetState: (state: RecordingWidgetState) => void;
    handleStartRecording: (params: HandleStartRecordingParams) => void;
    handleFinishRecording: () => void;
    // Returns true when it aborted a start that was only waiting on the EN-1383 warning (nothing was recording).
    cancelInFlightStartRecording: () => boolean;
    renderRecordingWidget: boolean;
    setRenderRecordingWidget: (render: boolean) => void;
    // EN-1383: true while a recording is running and the screen wake lock could not be kept.
    screenMayTurnOff: boolean;
};

// controls patient data
export type recordingPatientControls = {
    patientName: string;
    setPatientName: (name: string) => void;
    patientRecordId: string;
    setPatientRecordId: (id: string) => void;
    /** The recording visit's patient name when the recording started (pet names for Stop & Generate, EN-1492). */
    visitPatientName: string;
};

// controls file upload
export type recordingFileUploadControls = {
    uploadProgress: number;
    uploadingState: FileUploadingState;
    uploadMessage: { message: string; type: "info" | "error" | "warning" } | null;
    failedToUploadOrStoreRecording: File | null;
};

// controls document selection
export type recordingDocumentSelectControls = {
    setGenerateOnStop: (generate: boolean) => void;
};

export type documentAssetControls = {
    isUploading: boolean;
    setIsUploading: (isUploading: boolean) => void;
    uploadAssetFiles: (files: File[], documentAssets: DocumentAsset[], patientRecordId: string) => Promise<void>;
};

export interface CallTarget {
    patientRecordId?: string | null;
    patientName?: string | null;
    contact?: Contact | null;
    // True when patientRecordId was created as a placeholder for this call (quick-start),
    // rather than referring to a pre-existing patient. Lets cancellation cleanup know it's
    // safe to archive the record — never set for calls against an existing patient.
    isQuickStart?: boolean;
}

// phone call widget controls
export type phoneCallWidgetControls = {
    renderPhoneCallWidget: boolean;
    setRenderPhoneCallWidget: React.Dispatch<React.SetStateAction<boolean>>;
    callTarget: CallTarget | null;
    setCallTarget: React.Dispatch<React.SetStateAction<CallTarget | null>>;
    handleStartQuickStartCall: (patientName?: string) => Promise<boolean>;
    isStartingQuickStartCall: boolean;
    isCallConnected: boolean;
    setIsCallConnected: React.Dispatch<React.SetStateAction<boolean>>;
};

// recording provider context
interface AssetLibraryContext {
    recorderControls: recorderControls;
    recordingWidgetControls: recordingWidgetControls;
    recordingPatientControls: recordingPatientControls;
    recordingFileUploadControls: recordingFileUploadControls;
    recordingDocumentSelectControls: recordingDocumentSelectControls;
    documentAssetControls: documentAssetControls;
    phoneCallWidgetControls: phoneCallWidgetControls;
}

const AudioRecorderContext = createContext<AssetLibraryContext | undefined>(undefined);

export function useAssetLibraryProvider(): AssetLibraryContext {
    const context = useContext(AudioRecorderContext);
    if (!context) {
        throw new Error("useAssetLibraryProvider must be used within an AssetLibraryProvider");
    }
    return context;
}

export function AssetLibraryProvider({
    children,
    openVisit,
}: {
    children: React.ReactNode;
    /** Opens a visit page; lets a refused start offer the visit it was merged into. */
    openVisit?: (visitId: string) => void;
}) {
    const dispatch = useDispatch();

    /**
     * recording provider state
     **/
    const [recordingWidgetState, setRecordingWidgetState] = useState<RecordingWidgetState>(
        RecordingWidgetState.WAITING,
    );
    const [renderRecordingWidget, setRenderRecordingWidget] = useState<boolean>(false);
    const [patientRecordId, setPatientRecordId] = useState<string>("");
    const [visitPatientName, setVisitPatientName] = useState<string>("");
    /**
     * document Asset Upload State
     */
    const [isUploadingAssetDocument, setIsUploadingAssetDocument] = useState<boolean>(false);

    /**
     * Phone Call Widget State
     */
    const [renderPhoneCallWidget, setRenderPhoneCallWidget] = useState<boolean>(false);
    const [callTarget, setCallTarget] = useState<CallTarget | null>(null);
    const [isStartingQuickStartCall, setIsStartingQuickStartCall] = useState<boolean>(false);
    // setIsStartingQuickStartCall only takes effect on next render, so it can't stop a synchronous
    // double-invocation (e.g. rapid double-click) before the button's disabled state commits.
    const isStartingQuickStartCallRef = useRef<boolean>(false);
    const [isCallConnected, setIsCallConnected] = useState<boolean>(false);

    // Single source of truth for clearing callTarget: whenever the widget closes, for any
    // reason (cancel, call ended, or a future close path), callTarget must not persist into
    // the next time the widget opens — it outranks the live Redux active patient, so a stale
    // value would silently prefill or dial the wrong patient's number next open. Individual
    // close paths only need to call setRenderPhoneCallWidget(false); they must not also clear
    // callTarget themselves.
    useEffect(() => {
        if (!renderPhoneCallWidget) {
            setCallTarget(null);
        }
    }, [renderPhoneCallWidget]);

    /**
     * Providers and Hooks
     **/
    // REFS
    const {
        generateOnStopRef,
        selectedDocumentsRef,
        captureMonitoringDocumentIdForRecording,
        recordingMonitoringDocumentIdRef,
    } = useRealtimeContextApi();

    // AUDIO RECORDER
    const {
        startRecording,
        stopRecording,
        togglePauseResume,
        recordingState,
        setRecordingState,
        recordingTime,
        audioStream,
        activeRecordingId,
        setActiveRecordingId,
        resetRecorder,
        recordingMessage,
        setRecordingMessage,
        chunkBuffer,
        blobIndexRef,
        pauseResumeSupported,
        isExtensionTransport,
    } = useAudioRecorder({ recordingMonitoringDocumentIdRef });

    // PATIENTS
    const { patientName, setPatientName, clearFields, handleInsertPatientRecord } = usePatientState();
    // FILE UPLOAD
    // file upload provider
    const { uploadProgress, uploadingState, uploadMessage, uploadAudio, failedToUploadOrStoreRecording } =
        useFileUpload();

    // DATA PROVIDER
    const { selectedDate } = useDataContextApi();

    // ACCOUNT
    const { containsFeatureFlag, accountData } = useAccountContext();
    // The effective user (the impersonated one when impersonating) owns the EN-1383 acknowledgement.
    const { userId: effectiveUserId } = useImpersonationAwareUser();
    // The widget keys its minimized preference by the session user, so read it the same way.
    const sessionUserId = useSession()?.user?.id;
    const isNarrowViewport = useIsNarrowViewport();

    // REDUX
    const { selectedDoctor } = useAppSelector((state) => state.doctors);

    // CHUNK REFS
    // queue of chunk indices to retry
    const retryQueue = useRef<number[]>([]);

    // CONSTANTS
    // number of chunks actively being retried
    const activeRetryCount = useRef(0);

    // Cancellation flag for an in-flight handleStartRecording. Set by
    // cancelInFlightStartRecording() (e.g. when the user navigates away while the start is
    // waiting on the warning or connecting) and checked after the mobile warning resolves and
    // after `await startRecording` so the start path can bail out of the RECORDING transition
    // and tear down any partial resources via stopRecording.
    // Prevents the phantom-recording race where startAudioCapture resolves after a
    // parallel stopRecording, re-starting the timer and re-arming the silence watchdog.
    const startRecordingCancelledRef = useRef<boolean>(false);
    // Pending error-clear timer from a failed start; a new start cancels it so it cannot reset a live recording.
    const startErrorResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
        () => () => {
            if (startErrorResetTimerRef.current) clearTimeout(startErrorResetTimerRef.current);
        },
        [],
    );

    // EN-1499: held for the whole of one handleStartRecording (mic check, mobile warning, visit creation,
    // startRecording, the RECORDING/WAITING transition) and released in its finally on every exit. A second
    // press while it is held neither creates a second visit nor touches widget state. The list and calendar
    // record buttons are not disabled during a start, so this is the only double-press guard after the modal.
    const startInFlightRef = useRef<boolean>(false);
    const mic = useMic();

    const isRecording = useMemo(() => {
        return recordingState === RecordingState.recording || recordingState === RecordingState.paused;
    }, [recordingState]);

    // EN-1383: phone and tablet browsers record silence once the screen sleeps. Before the
    // first web recording on such a device, a blocking warning must be confirmed. The pending
    // promise resolves true on confirm, false if the start is cancelled (route change while
    // LOADING, or the app locking). Only one start can be parked at a time: a second start while
    // the modal is open is ignored (see handleStartRecording).
    const [mobileWarningOpen, setMobileWarningOpen] = useState(false);
    const mobileWarningRef = useRef<{ resolve: (confirmed: boolean) => void } | null>(null);

    const requestMobileBrowserRecordingConfirmation = useCallback((): Promise<boolean> => {
        // handleStartRecording never calls this while a warning is pending, so there is no promise to share.
        let resolve: (confirmed: boolean) => void = () => {};
        const promise = new Promise<boolean>((r) => {
            resolve = r;
        });
        mobileWarningRef.current = { resolve };
        setMobileWarningOpen(true);
        return promise;
    }, []);

    // Returns whether a warning was pending, so callers can tell "aborted a parked start" from "nothing to do".
    const settleMobileWarning = useCallback((confirmed: boolean): boolean => {
        const pending = mobileWarningRef.current;
        if (!pending) return false;
        mobileWarningRef.current = null;
        setMobileWarningOpen(false);
        if (!confirmed) {
            Sentry.addBreadcrumb({
                category: "recording",
                message: "recording.mobile_warning_aborted",
                level: "info",
            });
        }
        pending.resolve(confirmed);
        return true;
    }, []);

    // Never leave a start parked on a promise nobody can settle once the provider is gone.
    useEffect(() => () => void settleMobileWarning(false), [settleMobileWarning]);

    const handleConfirmMobileWarning = useCallback(() => {
        acknowledgeMobileBrowserRecording(effectiveUserId);
        settleMobileWarning(true);
    }, [settleMobileWarning, effectiveUserId]);

    // If the screen cannot be kept on, the widget shows a notice. It has its own state rather than the
    // shared recordingMessage slot, which other messages replace at any time. Non-blocking:
    // the recording continues. Cleared when a recording starts or stops. This effect is declared BEFORE
    // useScreenWakeLock so that a report the hook makes synchronously in its own effect (unsupported) is not wiped.
    const [screenMayTurnOff, setScreenMayTurnOff] = useState(false);
    useEffect(() => {
        setScreenMayTurnOff(false);
    }, [isRecording]);
    const handleWakeLockUnavailable = useCallback(() => setScreenMayTurnOff(true), []);

    // Safe to evaluate each render: UA, touch points and the extension-iframe flag are fixed for the page's lifetime.
    // isRecording is false until the recorder actually starts, so the lock is never taken while the warning is open.
    useScreenWakeLock(isRecording && isMobileBrowserRecordingDevice(), handleWakeLockUnavailable);

    // handle setting generate on stop
    const setGenerateOnStop = useCallback(
        (generate: boolean) => {
            generateOnStopRef.current = generate;
        },
        [generateOnStopRef],
    );

    /**
     * recording provider handlers
     **/

    const MAX_CONCURRENT_RETRIES = 2;
    const MAX_RETRIES = 10;
    const BASE_RETRY_DELAY = 1000;
    const MAX_RETRY_DELAY = 20000;

    /**
     * The retry queue works as follows:
     *
     * If a chunk fails to upload, it is added to the retry queue, and the retry process is started.
     * This function grabs the first index from the queue and finds the chunk in the buffer.
     *
     * It then calculates a delay based on the retry count and a base delay (between 1s and 20s).
     * 		- The delay is the min of base * 2^retryCount and max, plus some jitter.
     *
     * We schedule the upload with the given delay, and on success, we mark the chunk as successful.
     * On failure, we increment the retry count and requeue the chunk if we haven't reached the max retries.
     *
     * We then decrement the active retry count and process the next chunk in the queue in a recursive-esque manner.
     */
    const processRetryQueue = useCallback(async () => {
        // check max concurrent
        if (activeRetryCount.current >= MAX_CONCURRENT_RETRIES) {
            return;
        }

        // get the next chunk index
        const nextIndex = retryQueue.current.shift(); // FIFO
        if (!nextIndex) {
            // queue is empty
            return;
        }

        // find actual chunk
        const chunk = chunkBuffer.current.find((c) => c.chunkIndex === nextIndex);
        if (!chunk || chunk.success || chunk.uploading) {
            // chunk is already uploaded or uploading
            // try next chunk
            processRetryQueue();
            return;
        }

        // inc active count
        activeRetryCount.current++;

        // delay is min of base * 2^retryCount and max
        const delay = Math.min(
            BASE_RETRY_DELAY * Math.pow(2, chunk.retryCount) + Math.random() * 1000, // add jitter
            MAX_RETRY_DELAY,
        );

        // upload
        chunk.retryTimeoutId = setTimeout(async () => {
            try {
                chunk.uploading = true;
                const resp = await uploadChunk(
                    chunk.data,
                    chunk.transcriptionId,
                    chunk.transcriptSectionId,
                    chunk.patientRecordId,
                    chunk.chunkIndex,
                    false,
                );

                if (resp.error) {
                    // error
                    throw new Error(resp.error);
                }

                // success
                chunk.success = true;
                chunk.retryCount = 0;
            } catch (error) {
                // error
                Sentry.addBreadcrumb({
                    category: "recording",
                    message: "Error retrying chunk",
                    level: "error",
                    data: { error, chunkIndex: chunk.chunkIndex, transcriptSectionId: chunk.transcriptSectionId },
                });

                chunk.retryCount++;
                chunk.retryTimeoutId = undefined;

                // requeue
                if (chunk.retryCount < MAX_RETRIES) {
                    retryQueue.current.unshift(chunk.chunkIndex);
                }
            } finally {
                chunk.uploading = false;
                activeRetryCount.current--;
                processRetryQueue();
            }
        }, delay);

        // try next chunk
        processRetryQueue();
    }, [uploadChunk, chunkBuffer, retryQueue]);

    // handle recording complete
    const onAudioDataAvailableChunk = useCallback(
        async (
            audioFile: File,
            patientRecord: PatientRecord,
            transcriptSectionId: string,
            chunkIndex: number,
            end: boolean = false,
        ) => {
            try {
                if (!patientRecord.transcriptions?.id || !patientRecord.id) {
                    throw new Error("patientRecord is undefined");
                }

                const currentIndex = chunkIndex;
                let docIds: string[] = [];
                let docAssetsToUse: string[] = [];
                let tsIdsToUse: string[] = [];

                // if this is the last chunk, use the selected documents
                if (generateOnStopRef.current && end) {
                    docIds = selectedDocumentsRef.current;

                    // Auto-generation always uses all clips -- the server fetches them from the DB.
                    // User-curated selection only applies to the manual Generate button path.
                    docAssetsToUse = [];
                    tsIdsToUse = [];
                }

                try {
                    chunkBuffer.current[currentIndex].uploading = true;
                    const uploadResponse = await uploadChunk(
                        audioFile,
                        patientRecord.transcriptions.id,
                        transcriptSectionId,
                        patientRecord.id,
                        currentIndex,
                        end,
                        docIds,
                        tsIdsToUse,
                        docAssetsToUse,
                    );

                    // update transcript section for table
                    if (uploadResponse?.error) {
                        throw new Error(uploadResponse?.error);
                    }
                } finally {
                    chunkBuffer.current[currentIndex].uploading = false;
                }

                // update the chunk buffer success
                chunkBuffer.current[currentIndex].success = true;
            } catch (error) {
                Sentry.addBreadcrumb({
                    category: "recording",
                    message: "Error uploading chunk",
                    level: "error",
                    data: { error, chunkIndex, patientRecord, transcriptSectionId },
                });

                // add to retry queue
                if (!retryQueue.current.includes(chunkIndex)) {
                    retryQueue.current.push(chunkIndex);
                }

                // trigger retry processing
                processRetryQueue();
            } finally {
                // if this is the end, do clean up
                // (we also do cleanup in useAudioRecorder of the stuff initialized there, like chunkBuffer)
                if (end) {
                    // clear the retry queue
                    retryQueue.current = [];
                    // clear the active retry count
                    activeRetryCount.current = 0;
                }
            }
        },
        [uploadChunk, selectedDocumentsRef, generateOnStopRef, dispatch, processRetryQueue],
    );

    // handle recording complete
    const onAudioDataAvailableOld = async (
        audioFile: File,
        patientRecord: PatientRecord,
        transcriptSectionId: string,
        chunkIndex: number,
        _end: boolean = false,
    ) => {
        try {
            if (!patientRecord.transcriptions?.id || !patientRecord.id) {
                throw new Error("patientRecord is undefined");
            }

            // Explicit for clarity — this runs immediately after captureMonitoringDocumentIdForRecording
            // froze the ref for this exact recording, so it's equivalent to omitting the argument.
            const uploadResponse = await uploadAudio(
                audioFile,
                audioFile.name,
                patientRecord.transcriptions.id,
                transcriptSectionId,
                patientRecord,
                recordingMonitoringDocumentIdRef.current,
            );

            // update transcript section for table
            if (uploadResponse?.err) {
                throw new Error(uploadResponse?.err);
            }
        } catch (error) {
            Sentry.captureException(error);
        }
    };

    // EN-1499: end a session left in an error state (failed rollover or failed setup) before reporting a blocked press.
    // A retry from a failed hourly rollover still holds that session's refs and visit lease (kept so
    // Stop can read them). Leaving it when the retry is refused would let the next recording inherit them
    // and file its later hours under the old visit. The last hour already uploads from its own recorder.
    const endFailedSession = async () => {
        if (!isRecordingError(recordingState)) return;
        try {
            await stopRecording();
        } catch (e) {
            Sentry.captureException(e);
        }
    };

    // EN-1499: a press the mic blocked. Leave the widget idle and on screen, where the mic bar explains
    // why (list and calendar starts may have no widget open). When the bar will not be on screen
    // (the clinic hides the idle widget, or Quick Start is minimized), say it in a toast instead.
    const showMicBlocked = useCallback(
        (state: MicStateId) => {
            setRecordingWidgetState(RecordingWidgetState.WAITING);
            setRenderRecordingWidget(true);
            const minimized = sessionUserId
                ? readQuickStartMinimized(sessionUserId, isNarrowViewport)
                : isNarrowViewport;
            if (accountData?.meta_data?.floating_buttons_hidden === true || minimized) {
                const { title } = micCopy(state, mic.platform);
                if (title) toast.error(title);
            }
        },
        [accountData, mic.platform, sessionUserId, isNarrowViewport],
    );

    // handle start recording
    const handleStartRecording = useCallback(
        async ({ patientRecordId, patientName, showLoading = true }: HandleStartRecordingParams) => {
            // EN-1383: a start is already parked on the warning (double tap). The first caller owns it:
            // this one must not touch widget state, the cancel flag, the recording or the acknowledgement.
            if (mobileWarningRef.current) return;
            // EN-1499: an earlier press is still starting (mic check, visit creation or startRecording);
            // that press owns the start. Set below with no await in between, so two presses cannot both pass.
            if (startInFlightRef.current) return;
            startRecordingCancelledRef.current = false;
            // Freeze the Monitoring target at the moment recording actually starts — not whatever
            // tab happens to be open later when the upload kicks off. The recording widget persists
            // across tab navigation, so without this, switching tabs mid-recording would misattribute
            // (or drop) the clip's Monitoring target at stop-time instead of respecting the document
            // that was open when the tech started dictating.
            captureMonitoringDocumentIdForRecording();
            startInFlightRef.current = true;
            try {
                // EN-1499: check the mic before the mobile warning or creating a visit, so a blocked press
                // leaves nothing behind. The extension records its own audio, so it skips this check.
                if (!isExtensionTransport) {
                    // Callers such as WaitingView set RECORDING before calling; show LOADING while the
                    // check runs so the vet never sees a live Stop button for a start that has not begun.
                    if (showLoading) setRecordingWidgetState(RecordingWidgetState.LOADING);
                    const gate = await checkMicWithTimeout(mic.checkBeforeRecord);
                    if (!gate.ok) {
                        Sentry.addBreadcrumb({
                            category: "recording",
                            message: "recording.mic_gate_blocked",
                            level: "info",
                            data: { state: gate.state },
                        });
                        await endFailedSession();
                        showMicBlocked(gate.state);
                        return;
                    }
                    if (startRecordingCancelledRef.current) {
                        // Cancelled via cancelInFlightStartRecording while the check ran: nothing has started.
                        startRecordingCancelledRef.current = false;
                        setRecordingWidgetState(RecordingWidgetState.WAITING);
                        return;
                    }
                }

                // Gate the start only: pause/resume continue a recording the user already confirmed for.
                const needsMobileWarning =
                    isMobileBrowserRecordingDevice() && !hasAcknowledgedMobileBrowserRecording(effectiveUserId);

                // While the warning is open nothing has started, so the widget stays idle behind it
                // (callers such as WaitingView may already have set RECORDING). LOADING begins after confirm.
                if (showLoading) {
                    setRecordingWidgetState(
                        needsMobileWarning ? RecordingWidgetState.WAITING : RecordingWidgetState.LOADING,
                    );
                }

                if (needsMobileWarning) {
                    const confirmed = await requestMobileBrowserRecordingConfirmation();
                    if (!confirmed || startRecordingCancelledRef.current) {
                        startRecordingCancelledRef.current = false;
                        setRecordingWidgetState(RecordingWidgetState.WAITING);
                        return;
                    }
                    if (showLoading) {
                        setRecordingWidgetState(RecordingWidgetState.LOADING);
                    }
                }

                let patientRecord: PatientRecord | null = null;

                if (patientName) {
                    setPatientName(patientName);
                }

                if (!patientRecordId) {
                    // insert patient record
                    patientRecord = await handleInsertPatientRecord(true, patientName || undefined);

                    if (!patientRecord || !patientRecord.id || !patientRecord.transcriptions?.id) {
                        throw new Error("Error starting recording, failed to create patient record");
                    }

                    Sentry.addBreadcrumb({
                        category: "recording",
                        message: "Patient record created",
                        level: "info",
                        data: {
                            patientName,
                            providedPatientRecordId: patientRecordId,
                            createdPatientRecordId: patientRecord.id,
                            createdTranscriptionId: patientRecord?.transcriptions?.id,
                            selectedDate,
                            selectedDoctor,
                        },
                    });
                } else {
                    // fetch the patient record
                    const { data, error } = await fetchPatientRecord(patientRecordId);

                    if (error || !data) {
                        // A page opened before this visit was merged away: say so (page notice) instead
                        // of the generic failure.
                        const keptVisitId =
                            error === PatientRecordFetchErrors.NO_PATIENT_RECORD
                                ? await mergedIntoVisit(patientRecordId)
                                : null;
                        if (keptVisitId) {
                            throw Object.assign(new Error("Visit was merged away"), {
                                code: MERGED_AWAY_START,
                                keptVisitId,
                            });
                        }
                        throw new Error("Error starting recording, failed to fetch patient record");
                    }

                    patientRecord = data;
                }

                setPatientRecordId(patientRecord.id);
                // The name Transcribe reads (patient_name, else the linked patient's), for its per-pet generations.
                setVisitPatientName(patientRecord.patient_name || patientRecord.patients?.name || "");

                const transcriptSectionId = uuid();

                // FEATURE FLAG CHUNK UPLOAD
                const onDataAvailable = containsFeatureFlag(FeatureFlags.CHUNK_UPLOAD)
                    ? onAudioDataAvailableChunk
                    : onAudioDataAvailableOld;

                // Cancel only once this press really starts: a press refused earlier (mic, warning, merged
                // visit) leaves the previous failure on screen, and its timer must still clear it.
                if (startErrorResetTimerRef.current) {
                    clearTimeout(startErrorResetTimerRef.current);
                    startErrorResetTimerRef.current = null;
                }
                // start recording with audio recorder hook
                await startRecording(
                    patientRecord,
                    transcriptSectionId,
                    (
                        audioFile: File,
                        patientRecord: PatientRecord,
                        transcriptSectionId: string,
                        chunkIndex: number,
                        end: boolean,
                    ) => onDataAvailable(audioFile, patientRecord, transcriptSectionId, chunkIndex, end),
                );

                // If a cancellation was requested while `startRecording` was awaiting (e.g. user
                // navigated away during LOADING), do not transition to RECORDING. startRecording
                // may have already started the timer / armed the silence watchdog / set refs in
                // its extension branch, so stopRecording is needed to tear those down atomically
                // here instead of from a parallel handleFinishRecording call.
                if (startRecordingCancelledRef.current) {
                    startRecordingCancelledRef.current = false;
                    try {
                        await stopRecording({ discard: true });
                    } catch (cleanupError) {
                        Sentry.captureException(cleanupError);
                    }
                    clearFields();
                    setPatientRecordId("");
                    return;
                }

                setRecordingWidgetState(RecordingWidgetState.RECORDING);

                Sentry.addBreadcrumb({
                    category: "recording",
                    message: "Recording started",
                    level: "info",
                    data: { patientName, patientRecordId, selectedDate, selectedDoctor, transcriptSectionId },
                });
            } catch (error: any) {
                if (error instanceof MicStartError) {
                    // The start's own getUserMedia failed. The mic bar shows why, so no recording
                    // message and no exception report. A Quick Start visit created above is left in place as an empty visit; this path does not clean it up.
                    const state = mic.reportStartFailure(error.cause);
                    Sentry.addBreadcrumb({
                        category: "recording",
                        message: "recording.mic_start_blocked",
                        level: "info",
                        data: { state },
                    });
                    await endFailedSession();
                    showMicBlocked(state);
                    return;
                }
                if (error?.code === MERGED_AWAY_START) {
                    // Expected, so not reported. Started from the list or calendar there is no page notice:
                    // say why nothing started and offer the kept visit.
                    setRecordingWidgetState(RecordingWidgetState.WAITING);
                    if (openVisit) {
                        const keptVisitId: string = error.keptVisitId;
                        showActionToast({
                            kind: "warning",
                            message: COPY.startRefusedMergedAway,
                            actionLabel: COPY.recycleOpenKept(""),
                            onAction: () => openVisit(keptVisitId),
                        });
                    } else {
                        toast.warning(COPY.startRefusedMergedAway, { autoClose: 10_000 });
                    }
                    return;
                }
                Sentry.captureException(error);
                // set an error message
                setRecordingMessage({
                    message: error?.message ? error?.message : "An unexpected error occurred.",
                    type: "error",
                });
                // set the recording widget state to waiting (reset the display)
                setRecordingWidgetState(RecordingWidgetState.WAITING);
                // Force-clear the error after the visibility window has elapsed.
                // resetRecorder(true) bypasses the error-durability guard in useAudioRecorder,
                // which would otherwise preserve the error message indefinitely.
                if (startErrorResetTimerRef.current) clearTimeout(startErrorResetTimerRef.current);
                startErrorResetTimerRef.current = setTimeout(() => {
                    startErrorResetTimerRef.current = null;
                    resetRecorder(true);
                }, 3000);
            } finally {
                startInFlightRef.current = false;
            }
        },
        [
            recordingState,
            selectedDate,
            selectedDoctor,
            resetRecorder,
            handleInsertPatientRecord,
            fetchPatientRecord,
            startRecording,
            stopRecording,
            clearFields,
            onAudioDataAvailableChunk,
            onAudioDataAvailableOld,
            setRecordingWidgetState,
            setPatientRecordId,
            setPatientName,
            requestMobileBrowserRecordingConfirmation,
            effectiveUserId,
            openVisit,
            isExtensionTransport,
            mic,
            showMicBlocked,
        ],
    );

    // handle starting a call from the floating quick-start widget with no existing
    // patient selected — creates the same kind of placeholder patient record
    // freeform recordings get today (handleInsertPatientRecord), then opens the
    // dialer against it with no linked contact (phone field starts empty)
    const handleStartQuickStartCall = useCallback(
        async (patientNameArg?: string) => {
            if (isStartingQuickStartCallRef.current) {
                return false;
            }
            isStartingQuickStartCallRef.current = true;
            setIsStartingQuickStartCall(true);
            try {
                const patientRecord = await handleInsertPatientRecord(true, patientNameArg || undefined);

                if (!patientRecord || !patientRecord.id) {
                    throw new Error("Error starting call, failed to create patient record");
                }

                setCallTarget({
                    patientRecordId: patientRecord.id,
                    patientName: patientNameArg || null,
                    contact: null,
                    isQuickStart: true,
                });
                voipAnalytics.widgetOpened({ source: "quick_start" });
                setRenderPhoneCallWidget(true);
                return true;
            } catch (error) {
                Sentry.captureException(error);
                toast.error("Failed to start call");
                return false;
            } finally {
                setIsStartingQuickStartCall(false);
                isStartingQuickStartCallRef.current = false;
            }
        },
        [handleInsertPatientRecord, setCallTarget, setRenderPhoneCallWidget],
    );

    // Signal that an in-flight handleStartRecording should abort its transition to RECORDING
    // and clean up any partial state after the ongoing `await startRecording` resolves.
    // Safe to call at any time; no-op if no start is in flight.
    // A start waiting on the EN-1383 warning is aborted too: close it, record nothing, store no
    // acknowledgement. Returns true in that case, so the caller can skip a "recording stopped" message.
    const cancelInFlightStartRecording = useCallback((): boolean => {
        startRecordingCancelledRef.current = true;
        return settleMobileWarning(false);
    }, [settleMobileWarning]);

    // handle finish recording
    const handleFinishRecording = async () => {
        try {
            await stopRecording();
        } catch {
            // stopRecording already set recordingMessage; reset widget so user doesn't see false SAVED.
            clearFields();
            setPatientRecordId("");
            setRecordingWidgetState(RecordingWidgetState.WAITING);
            return;
        }

        clearFields();
        setPatientRecordId("");

        Sentry.addBreadcrumb({
            category: "recording",
            message: "Recording stopped",
            level: "info",
            data: { patientRecordId, selectedDate, selectedDoctor },
        });
    };

    // safe toggle pause resume
    const safeTogglePauseResume = useCallback(async () => {
        // try to toggle pause/resume
        try {
            if (isRecordingError(recordingState)) {
                handleStartRecording({ patientRecordId, patientName, showLoading: false });
                return;
            }

            // Extension path does not support pause/resume — silently no-op.
            // The UI button is also disabled (disabled={!pauseResumeSupported}) so this
            // guard is a belt-and-suspenders defence against programmatic calls.
            if (!pauseResumeSupported) {
                return;
            }

            await togglePauseResume();

            setRecordingWidgetState(
                recordingWidgetState === RecordingWidgetState.PAUSED
                    ? RecordingWidgetState.RECORDING
                    : RecordingWidgetState.PAUSED,
            );

            Sentry.addBreadcrumb({
                category: "recording",
                message: "Recording paused/resumed",
                level: "info",
                data: { patientRecordId, selectedDate, selectedDoctor, recordingWidgetState },
            });
        } catch (error) {
            // if an error has occurred, handle it by stopping the recording

            // log the error to sentry
            Sentry.captureException(error);

            // set recording widget state to uploading
            setRecordingWidgetState(RecordingWidgetState.UPLOADING);

            // end the recording
            handleFinishRecording();

            // notify the user of the error
            toast.error("An unexpected error occurred while pausing the recording");
        }
    }, [
        recordingState,
        resetRecorder,
        togglePauseResume,
        pauseResumeSupported,
        patientRecordId,
        patientName,
        recordingWidgetState,
        selectedDate,
        selectedDoctor,
        handleStartRecording,
        handleFinishRecording,
    ]);

    /*
     * Document Asset Upload Functions
     */
    // upload document asset files
    const uploadAssetFiles = useCallback(
        async (files: File[], documentAssets: DocumentAsset[], patientRecordId: string) => {
            if (files.length === 0) return;

            // set the upload state to true
            setIsUploadingAssetDocument(true);

            // Create initial file entries in state
            const newFiles: DocumentAsset[] = files.map((file) => ({
                id: uuid(),
                created_at: new Date().toISOString(),
                file_name: file.name,
                file_size: file.size,
                file_type: file.type,
                status: DocumentAssetStatus.Uploading,
            }));

            // Update the state to include the new assets
            dispatch(setDocumentAssets([...documentAssets, ...newFiles]));

            // Upload each file
            let updatedAssets = [...documentAssets, ...newFiles];

            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const newFileIndex = documentAssets.length + i;

                if (file) {
                    // upload the documents
                    const { data, err: uploadError } = await UploadDocumentAsset(patientRecordId || "", file);
                    if (uploadError) {
                        // set the status to failed
                        updatedAssets = [
                            ...updatedAssets.slice(0, newFileIndex),
                            {
                                ...newFiles[i],
                                status: DocumentAssetStatus.Failed,
                                uploadErrorMessage: uploadErrorMessageFor(uploadError),
                            },
                            ...updatedAssets.slice(newFileIndex + 1),
                        ];
                        dispatch(setDocumentAssets(updatedAssets));
                        continue;
                    }

                    // update the state of the asset to uploaded
                    if (data?.id) {
                        updatedAssets = [
                            ...updatedAssets.slice(0, newFileIndex),
                            {
                                ...newFiles[i],
                                status: DocumentAssetStatus.Uploaded,
                                id: data.id,
                                // The server adds a random suffix to the object name, so View File needs
                                // the stored path; without it a new upload only opens after a reload. If a
                                // response ever lacks it, ThreeDotDocument falls back to rebuilding the path
                                // from file_name, the behavior before this line.
                                storage_path: data.storage_path,
                            },
                            ...updatedAssets.slice(newFileIndex + 1),
                        ];
                        dispatch(setDocumentAssets(updatedAssets));
                        dispatch(toggleAssetSelection(data.id));
                    }
                }
            }

            // set the upload state to false
            setIsUploadingAssetDocument(false);
        },
        [dispatch, setIsUploadingAssetDocument],
    );

    /**
     * provider controls
     **/
    // Register prepare-for-lock: stop recording and wait for uploads before lock (PR 7)
    const { registerPrepareForLock, isLocked } = useLock();
    useEffect(() => {
        registerPrepareForLock(async () => {
            // A start still waiting on the EN-1383 warning must not survive the lock: another
            // member may unlock, and confirming would record into the previous member's visit.
            // settleMobileWarning is a no-op when no start is pending; the cancel flag is reset at the next start.
            cancelInFlightStartRecording();
            // Only a start still loading falls back to WAITING; a live recording keeps its widget
            // state in case the lock aborts after a failed stop.
            setRecordingWidgetState((state) =>
                state === RecordingWidgetState.LOADING ? RecordingWidgetState.WAITING : state,
            );
            await stopRecording();
            await waitForUploadsToComplete();
        });
        return () => registerPrepareForLock(() => Promise.resolve());
    }, [registerPrepareForLock, stopRecording, cancelInFlightStartRecording, setRecordingWidgetState]);

    // LockProvider skips prepare-for-lock while the recorder is idle, which is the state of a start
    // parked on the EN-1383 warning. Settle it here so unlocking cannot reopen the modal for the next member.
    useEffect(() => {
        if (isLocked) settleMobileWarning(false);
    }, [isLocked, settleMobileWarning]);

    // control the recording hook
    const recorderControlsValue: recorderControls = {
        startRecording,
        stopRecording,
        togglePauseResume: safeTogglePauseResume,
        isRecording,
        recordingState,
        recordingTime,
        audioStream,
        activeRecordingId,
        setActiveRecordingId,
        resetRecorder,
        setRecordingState,
        recordingMessage,
        setRecordingMessage,
        chunkBuffer,
        blobIndexRef,
        pauseResumeSupported,
        isExtensionTransport,
    };

    // control the recording widget
    const recordingWidgetControlsValue: recordingWidgetControls = {
        recordingWidgetState,
        setRecordingWidgetState,
        handleStartRecording,
        handleFinishRecording,
        cancelInFlightStartRecording,
        renderRecordingWidget,
        setRenderRecordingWidget,
        screenMayTurnOff,
    };

    // control the patient data
    const recordingPatientControlsValue: recordingPatientControls = {
        patientName,
        setPatientName,
        patientRecordId,
        setPatientRecordId,
        visitPatientName,
    };

    // control the file upload
    const recordingFileUploadControlsValue: recordingFileUploadControls = {
        uploadProgress,
        uploadingState,
        uploadMessage,
        failedToUploadOrStoreRecording,
    };

    // control the document selection
    const recordingDocumentSelectControlsValue: recordingDocumentSelectControls = {
        setGenerateOnStop: setGenerateOnStop,
    };

    // control the document asset upload
    const documentAssetControlsValue: documentAssetControls = {
        isUploading: isUploadingAssetDocument,
        setIsUploading: setIsUploadingAssetDocument,
        uploadAssetFiles,
    };

    // control the phone call widget
    const phoneCallWidgetControlsValue: phoneCallWidgetControls = {
        renderPhoneCallWidget,
        setRenderPhoneCallWidget,
        callTarget,
        setCallTarget,
        handleStartQuickStartCall,
        isStartingQuickStartCall,
        isCallConnected,
        setIsCallConnected,
    };

    // render the RecordingWidget with audio recorder provider as context
    // this ensures there is only one recording widget per audio recording provider
    // (and as there is only one instance of audio recording provider, there is only one recording widget)
    return (
        <AudioRecorderContext.Provider
            value={{
                recorderControls: recorderControlsValue,
                recordingWidgetControls: recordingWidgetControlsValue,
                recordingPatientControls: recordingPatientControlsValue,
                recordingFileUploadControls: recordingFileUploadControlsValue,
                recordingDocumentSelectControls: recordingDocumentSelectControlsValue,
                documentAssetControls: documentAssetControlsValue,
                phoneCallWidgetControls: phoneCallWidgetControlsValue,
            }}
        >
            {children}
            <MobileBrowserRecordingWarning
                // isLocked is read at render (reactive); the prepare-for-lock callback cancels the pending start.
                isOpen={mobileWarningOpen && !isLocked}
                storeUrl={storeUrlForDevice()}
                onConfirm={handleConfirmMobileWarning}
            />
        </AudioRecorderContext.Provider>
    );
}
